//
// Copyright (c) 2019-Present Syncari - All rights reserved.
//
import { useCallback } from 'react';
import { SelectValue } from 'antd/lib/select';

import { PicklistValue } from 'components/inputs/types';

import { FuzzyOptionState } from '../types';
import {
  formatFuzzyValue,
  getInitialFuzzyValue,
  parseFuzzyValue,
  shouldResetToDefault,
} from '../utils/fuzzyValueHelpers';

/**
 * Custom hook to handle fuzzy option selection logic
 * Manages option clicks and slider changes
 */
export const useFuzzySelection = (
  selectedValue: SelectValue | undefined,
  expandedFuzzyOption: FuzzyOptionState | null,
  setSelectedValue: (value: SelectValue) => void,
  setExpandedFuzzyOption: (state: FuzzyOptionState | null) => void,
  setIsOpen: (open: boolean) => void,
  setSearchValue: (value: string) => void,
  onChange?: (value: SelectValue) => void
) => {
  // Handle option selection
  const handleOptionSelect = useCallback(
    (option: PicklistValue, isFuzzy: boolean) => {
      if (isFuzzy) {
        // For fuzzy options, expand the slider and set initial value
        const valStr = typeof selectedValue === 'string' ? selectedValue : String(selectedValue || '');
        const { value: currentBaseValue, percentage, typoCount } = parseFuzzyValue(valStr);
        const isTypoConfig = option.hasMinMaxTypoConfig ?? false;

        // Check if we're selecting a different option - if so, reset to defaults
        const isSameOption = !shouldResetToDefault(currentBaseValue, option.value);

        const initialValue = getInitialFuzzyValue(isTypoConfig, isSameOption, isTypoConfig ? typoCount : percentage);

        if (isTypoConfig) {
          // Set the fuzzy option state for typo count
          setExpandedFuzzyOption({
            option,
            percentage: null,
            typoCount: initialValue,
          });
        } else {
          // Set the fuzzy option state for percentage
          setExpandedFuzzyOption({
            option,
            percentage: initialValue,
            typoCount: null,
          });
        }

        // Immediately set the value with initial fuzzy value
        const formattedValue = formatFuzzyValue(option.value as string, initialValue);
        setSelectedValue(formattedValue);
        onChange?.(formattedValue);
      } else {
        // For regular options, select immediately and close
        setExpandedFuzzyOption(null);
        setSelectedValue(option.value);
        setIsOpen(false);
        setSearchValue('');
        onChange?.(option.value);
      }
    },
    [onChange, selectedValue, setExpandedFuzzyOption, setIsOpen, setSearchValue, setSelectedValue]
  );

  // Handle slider change - update value in real-time
  const handleSliderChange = useCallback(
    (newValue: number) => {
      if (expandedFuzzyOption && expandedFuzzyOption.option) {
        const isTypoConfig = expandedFuzzyOption.option.hasMinMaxTypoConfig ?? false;

        if (isTypoConfig) {
          // Update typo count
          setExpandedFuzzyOption({
            ...expandedFuzzyOption,
            typoCount: newValue,
          });
        } else {
          // Update percentage
          setExpandedFuzzyOption({
            ...expandedFuzzyOption,
            percentage: newValue,
          });
        }

        const formattedValue = formatFuzzyValue(expandedFuzzyOption.option.value as string, newValue);
        setSelectedValue(formattedValue);
        onChange?.(formattedValue);
      }
    },
    [expandedFuzzyOption, onChange, setExpandedFuzzyOption, setSelectedValue]
  );

  return {
    handleOptionSelect,
    handleSliderChange,
  };
};
